import { useMode } from '../lib/useMode';
import { useAuth } from '../lib/auth';
import { useTenantSettings } from '../lib/tenantSettings';

/** The Test/Live switch itself (sidebar) - lib/mode.ts owns the actual state.
 *
 * Dashboard brief task 1 ("Hide Test Mode"): some tenants should never see this switch at all -
 * gated by the ASSUMED `tenant_settings.hideModeSwitch` flag (see lib/tenantSettings.tsx and
 * lib/types.ts's `TenantSettings` doc comment). This never changes what mode a request actually
 * uses server-side (lib/mode.ts's own doc comment: "never weakens anything server-side") - it only
 * hides the staff-facing control for a tenant Loctopus has configured to not show it. */
export function ModeSwitch() {
  const [mode, setMode] = useMode();
  const { hideModeSwitch } = useTenantSettings();

  if (hideModeSwitch) return null;

  return (
    <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-100">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Mode</span>
      <div className="flex rounded-md border border-slate-200 overflow-hidden text-xs font-medium">
        <button
          type="button"
          onClick={() => setMode('test')}
          aria-pressed={mode === 'test'}
          className={`px-3 py-1 transition ${
            mode === 'test' ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          Test
        </button>
        <button
          type="button"
          onClick={() => setMode('live')}
          aria-pressed={mode === 'live'}
          className={`px-3 py-1 transition ${
            mode === 'live' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          Live
        </button>
      </div>
    </div>
  );
}

/** Banner shown across the top of every page while Test mode is active - the loud, hard-to-miss
 * half of this fix (a quiet badge in the corner is too easy to miss before, say, processing a
 * "refund" that's actually against fake test-mode money, or - worse - assuming a quiet Orders page
 * means there are no real orders). */
export function TestModeBanner() {
  const [mode] = useMode();
  const { hideModeSwitch } = useTenantSettings();
  // Same gate as `ModeSwitch` above, same reasoning (dashboard brief task 1) - a tenant with the
  // switch hidden never sees the banner either, since they can never leave Live mode to begin
  // with (the switch controlling mode is itself hidden).
  if (hideModeSwitch) return null;
  if (mode !== 'test') return null;
  return (
    <div className="bg-amber-400 text-amber-950 text-sm font-semibold text-center py-1.5 px-4">
      TEST MODE — you are viewing test data only. Switch to Live in the sidebar to see real orders.
    </div>
  );
}

/**
 * Dashboard brief task 2 ("Setup mode banner"): a small, neutral, non-scary notice shown while a
 * tenant isn't live yet - ASSUMED signal, `tenantStatus !== 'active'` (see lib/auth.tsx's
 * `AuthState.tenantStatus` and lib/types.ts's `TenantStatus` doc comment for exactly what's
 * assumed and why: `/dashboard/me` doesn't expose tenant status at all today).
 *
 * Deliberately plain/neutral styling (slate, not amber/red) - this is a normal, expected state for
 * a brand-new tenant still being set up, not a warning the way TestModeBanner's amber is. Renders
 * independently of `hideModeSwitch` - hiding the Test/Live switch is a separate decision from
 * telling staff payments aren't live yet.
 */
export function SetupModeBanner() {
  const { tenantStatus, isLoading } = useAuth();
  if (isLoading || tenantStatus === null) return null;
  if (tenantStatus === 'active') return null;
  return (
    <div className="bg-slate-100 text-slate-600 text-sm text-center py-1.5 px-4 border-b border-slate-200">
      Setup mode: payments are not live yet.
    </div>
  );
}
